import React from 'react';
import { Task } from '../types';
import TaskCard from './TaskCard';
import EmptyState from '../../../UI/EmptyState';
import styles from '../../../../styles/TaskManagement.module.css';

interface TaskListProps {
  tasks: Task[];
  loading: boolean;
  error: any;
  isInitialLoad: boolean;
  onTaskClick: (task: Task) => void;
  onRetry: () => void;
  isWorkMode?: boolean;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  showEditDelete?: boolean | ((task: Task) => boolean);
  embedded?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  loading,
  error,
  isInitialLoad,
  onTaskClick,
  onRetry,
  isWorkMode = false,
  onEdit,
  onDelete,
  showEditDelete = false,
  embedded = false,
  emptyStateTitle,
  emptyStateDescription
}) => {
  // ... (skeleton loading logic remains same, skipping for brevity in replacement if possible, but replace_file_content needs contiguous block)
  // Actually, I should target the interface and the component definition start, and then the return block separately if possible, or just the props destructuring and the EmptyState usage.
  // Let's do it in two chunks or one larger chunk if needed.
  // The file is small enough. I'll replace the interface and destructuring first.

  if (isInitialLoad || loading) {
    // ...
    return (
      <div className={styles.taskList}>
        {/* ... skeletons ... */}
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className={styles.taskCardSkeleton}>
            <div className={styles.skeletonCardHeader}>
              <div className={styles.skeletonCardHeaderLeft}>
                <div className={styles.skeletonIconWrapper}></div>
                <div className={styles.skeletonTitleSection}>
                  <div className={styles.skeletonTitle}></div>
                  <div className={styles.skeletonDescription}></div>
                </div>
              </div>
              <div className={styles.skeletonStatusBadge}></div>
            </div>
            <div className={styles.skeletonCardBody}>
              <div className={styles.skeletonTags}>
                <div className={styles.skeletonTag}></div>
                <div className={styles.skeletonTag}></div>
                <div className={styles.skeletonTag}></div>
                <div className={styles.skeletonTag}></div>
              </div>
              <div className={styles.skeletonMetadata}>
                <div className={styles.skeletonMetadataItem}></div>
                <div className={styles.skeletonMetadataItem}></div>
                <div className={styles.skeletonMetadataItem}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.taskList}>
        <EmptyState
          icon="⚠️"
          title="Failed to Load Tasks"
          description="Unable to load your tasks. Please check your connection and try again."
          action={{
            label: "Retry Loading",
            onClick: onRetry
          }}
        />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className={styles.taskList}>
        <EmptyState
          image="/images/notasks.png"
          title={emptyStateTitle || (isWorkMode ? "No Tasks Created" : "No Tasks Assigned")}
          description={
            emptyStateDescription || (isWorkMode
              ? "You haven't created any tasks yet. Click 'Create Task' to get started and assign tasks to your team members."
              : embedded
                ? "This user doesn't have any assigned tasks yet. Tasks assigned by higher authorities will appear here."
                : "You don't have any assigned tasks yet. Tasks assigned to you by higher authorities will appear here.")
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.taskList}>
      {tasks.map((task: Task) => {
        const shouldShowEditDelete = typeof showEditDelete === 'function'
          ? showEditDelete(task)
          : showEditDelete;

        return (
          <TaskCard
            key={task.id}
            task={task}
            onClick={onTaskClick}
            onEdit={onEdit}
            onDelete={onDelete}
            showEditDelete={shouldShowEditDelete}
            isWorkMode={isWorkMode}
          />
        );
      })}
    </div>
  );
};

export default TaskList;

